// Pure reducer for basket country/county updates used by Payment.updateAndRefreshBasket.
//
// It intentionally contains no React or network code so the address-preservation
// rules can be unit tested in isolation.
//
// Rules:
// - A latest (or independently entered) billing address must never be overwritten by
//   delivery changes or promo/session refreshes. This is gated on usingLatestBillingDetails.
// - Latest-address initialization always runs on mount/refresh and is responsible for
//   (re)applying the destination country (falling back to the basket's own country),
//   plus the latest delivery/billing county and billing country.

export function computeUpdatedBasket({
  sourceBasket,
  type,
  countryCode,
  countyCode,
  usingLatestBillingDetails,
  usingLatestDeliveryDetails,
  // When session delivery details exist, delivery is driven by those saved values,
  // not by the latest address. latestAddressInit then applies only latest billing
  // and leaves the delivery destination untouched.
  hasSessionDeliveryDetails = false,
}) {
  const updatedBasket = { ...sourceBasket };

  switch (type) {
    case "destinationcountrycode":
      updatedBasket.destinationcountrycode = countryCode;
      updatedBasket.destinationcountycode = "";

      // A latest billing address is independent from delivery. Preserve its
      // country and county when the delivery country changes.
      if (!usingLatestBillingDetails) {
        updatedBasket.origincountrycode = countryCode;
        if (updatedBasket?.collectbillingaddress) {
          updatedBasket.origincountycode = "";
        }
      }
      break;

    case "origincountrycode":
      updatedBasket.origincountrycode = countryCode;
      updatedBasket.origincountycode = "";
      break;

    case "sameAsDelivery":
      // Used when billing "same as delivery" is checked
      updatedBasket.origincountrycode = countryCode;
      updatedBasket.origincountycode = updatedBasket.destinationcountycode || "";
      break;

    case "origincountycode":
      updatedBasket.origincountycode = countyCode;
      break;

    case "destinationcountycode":
      updatedBasket.destinationcountycode = countyCode;
      if (updatedBasket?.collectbillingaddress && !usingLatestBillingDetails) {
        updatedBasket.origincountycode = countyCode;
      }
      break;

    case "latestAddressInit": {
      // Latest-address init always runs on mount/refresh, so it is also
      // responsible for (re)applying the destination country that the old
      // "resetting" refresh used to set. Use the latest delivery country when
      // available, otherwise fall back to the basket's own country.
      //
      // Skipped when session delivery details are present, so the delivery
      // destination country/county set from the session is not overwritten.
      if (!hasSessionDeliveryDetails) {
        const deliveryCountry = countryCode || updatedBasket.countrycode;
        if (deliveryCountry) {
          updatedBasket.destinationcountrycode = deliveryCountry;
          // Keep origin in sync only when billing is not an independent
          // latest/manual address.
          if (!usingLatestBillingDetails) {
            updatedBasket.origincountrycode = deliveryCountry;
          }
        }

        if (countyCode?.delivery) {
          updatedBasket.destinationcountycode = countyCode.delivery;
        }
      }

      // Apply latest billing values regardless of who owns this init.
      if (countyCode?.billingCountry) {
        updatedBasket.origincountrycode = countyCode.billingCountry;
      }
      if (countyCode?.billing) {
        updatedBasket.origincountycode = countyCode.billing;
      }
      break;
    }

    case "latestBillingInit":
      // Used when delivery address is invalid but billing is valid
      if (countryCode) {
        updatedBasket.origincountrycode = countryCode;
      }
      if (countyCode) {
        updatedBasket.origincountycode = countyCode;
      }
      break;

    case "resetting":
      // A reset refresh (promo apply/remove, session/discount restore) must still
      // persist the rest of the basket. It should only touch the delivery country
      // and county when latest delivery is NOT already driving those values; when
      // latest delivery is active, latestAddressInit already set the correct
      // destination, so preserve it here.
      if (!usingLatestDeliveryDetails) {
        updatedBasket.destinationcountrycode = countryCode;

        if (updatedBasket?.isdeliverypostaladdressrequired) {
          updatedBasket.destinationcountycode = countyCode;
        }
      }

      // Promo/session refreshes should not replace an independent latest
      // billing address with the delivery country or county.
      if (!usingLatestBillingDetails) {
        updatedBasket.origincountrycode = countryCode;
        if (updatedBasket?.collectbillingaddress) {
          updatedBasket.origincountycode = countyCode;
        }
      }
      break;

    default:
      break;
  }

  return updatedBasket;
}
